import React, { useState } from 'react';
import { withFormik } from 'formik';
import * as yup from 'yup';
import MediaContainer from '../../../../../shared/state/containers/MediaContainer';
import AutoGenerateFormFieldResponses from './AutoGenerateFormFieldResponses';
import AppliedApplicationTemplateContainer from '../../../../../shared/state/containers/AppliedApplicationTemplateContainer';
import { AppendedSection } from '../ApplicationManagerContent';
import TemplateFormSectionModel from '../../../../../shared/models/templating/api/TemplateFormSectionModel';
import { AutoGenerateAppliedAppValuesRequest } from '../../../../../shared/models/templating/api/AutoGenerateAppliedAppValuesRequest';
import AutoGenerateFormFieldResponsesModal, { AutoGenerateFormFieldResponsesModalProps, AutoGenerateFormFieldResponsesModalPublicProps } from './AutoGenerateFormFieldResponsesModal';
import AppliedApplicationTemplateModel from '../../../../../shared/models/templating/api/AppliedApplicationTemplateModel';
import IResult from '../../../../../shared/models/result/IResult';
import { computeFieldResults, FieldGenerateResult } from './autoGenerateResultUtils';
import AutoGenerateResultsModal from './AutoGenerateResultsModal';

// Public props for the wizard-step variant (used by ApplicationManagerContent)
export interface AutoGenerateFormFieldResponsesProps {
    applicationId: string
    handlePreviousSection: () => void
    handleNextSection: () => void
    mediaContainer: MediaContainer
    appliedAppTemplateContainer: AppliedApplicationTemplateContainer
    appendedSections: AppendedSection[]
    setAppendedSections: (sections: AppendedSection[]) => void
    incompleteSections: TemplateFormSectionModel[]
    setIncompleteSections: (sections: TemplateFormSectionModel[]) => void
    autoGenerateFormResponsesSection: AppendedSection
    handleRemoveSection: (sectionToRemove: AppendedSection) => void
    removeSectionFromIncompleteSections: (sectionToRemove: AppendedSection) => void
    isLoading: boolean
    setIsLoading: (value: boolean) => void
    onAutoGenerateComplete: (result: IResult<AppliedApplicationTemplateModel[]> | undefined, beforeSnapshot: AppliedApplicationTemplateModel[]) => void
}

// Internal-only props added by the wrapper before passing to the inner Formik component
export interface AutoGenerateFormFieldResponsesInternalProps extends AutoGenerateFormFieldResponsesProps {
    // onAutoGenerateComplete is already on the public props — no extra fields needed here
}

export interface AutoGenerateFormFieldResponsesFormData {
    autoGenerateFormFieldResponses: boolean
    usefileUpload: boolean
    fileUrl: string
    webPageUrls: string[]
}

interface Values {
    autoGenerateFormFieldResponses: boolean,
    usefileUpload: boolean,
    fileUrl: string,
    webPageUrls: string[]
}

async function handleSubmit(values: Values, { props }) {
    if (values.autoGenerateFormFieldResponses) {
        if ("setIsLoading" in props) {
            props.setIsLoading(true);
        }
        const appliedTemplatesToUpdate = props.appliedAppTemplateContainer?.state?.appliedTemplates ?? [];
        // Deep-clone before state so the container's in-place mutations don't corrupt our snapshot
        const beforeSnapshot: AppliedApplicationTemplateModel[] = JSON.parse(JSON.stringify(appliedTemplatesToUpdate));
        const appliedTemplatesToUpdateIds = appliedTemplatesToUpdate.map(template => template.id);
        const autoGenerateAppliedAppValuesRequest: AutoGenerateAppliedAppValuesRequest = {
            appliedApplicationTemplateIds: appliedTemplatesToUpdateIds,
            fileUrl: values.fileUrl,
            webPageUrls: values.webPageUrls
        };
        const result = await props.appliedAppTemplateContainer.autoGenerateAppliedApplicationTemplateFieldValues(props.applicationId, autoGenerateAppliedAppValuesRequest);
        if ("setIsLoading" in props) {
            props.setIsLoading(false);
        }
        props.onAutoGenerateComplete(result, beforeSnapshot);
    } else {
        // Auto-generate toggled off — skip the API call and proceed directly
        if ("onClose" in props) {
            props.onClose();
        }
        if ("handleNextSection" in props) {
            props.handleNextSection();
        }
    }
}

// Wizard-step Formik wrapper — passes onAutoGenerateComplete through from public props
const AutoGenerateFormFieldResponsesForm = withFormik<AutoGenerateFormFieldResponsesProps, AutoGenerateFormFieldResponsesFormData>({
    mapPropsToValues: props => ({
        autoGenerateFormFieldResponses: false,
        usefileUpload: false,
        fileUrl: '',
        webPageUrls: ['']
    }),
    validationSchema: yup.object().shape({}),
    handleSubmit
})(AutoGenerateFormFieldResponses);

// Modal variant inner Formik component (uses internal props including onAutoGenerateComplete)
const AutoGenerateFormFieldResponsesFormModalInner = withFormik<AutoGenerateFormFieldResponsesModalProps, AutoGenerateFormFieldResponsesFormData>({
    mapPropsToValues: props => ({
        autoGenerateFormFieldResponses: true,
        usefileUpload: false,
        fileUrl: '',
        webPageUrls: ['']
    }),
    validationSchema: yup.object().shape({}),
    handleSubmit
})(AutoGenerateFormFieldResponsesModal);

// Public wrapper for the modal variant: owns results modal state so callers only pass public props
const AutoGenerateFormFieldResponsesFormModal: React.FC<AutoGenerateFormFieldResponsesModalPublicProps> = (props) => {
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [successFields, setSuccessFields] = useState<FieldGenerateResult[]>([]);
    const [failedFields, setFailedFields] = useState<FieldGenerateResult[]>([]);
    const [hasApiError, setHasApiError] = useState(false);
    const [apiErrors, setApiErrors] = useState<string[]>([]);

    const handleAutoGenerateComplete = (
        result: IResult<AppliedApplicationTemplateModel[]> | undefined,
        beforeSnapshot: AppliedApplicationTemplateModel[]
    ) => {
        const computed = computeFieldResults(beforeSnapshot, result);
        setSuccessFields(computed.successFields);
        setFailedFields(computed.failedFields);
        setHasApiError(computed.hasApiError);
        setApiErrors(computed.apiErrors);
        setShowResultsModal(true);
    };

    const handleResultsConfirm = () => {
        setShowResultsModal(false);
        props.onClose();
    };

    return (
        <>
            <AutoGenerateFormFieldResponsesFormModalInner
                {...props}
                onAutoGenerateComplete={handleAutoGenerateComplete}
            />
            <AutoGenerateResultsModal
                showModal={showResultsModal}
                onConfirm={handleResultsConfirm}
                successFields={successFields}
                failedFields={failedFields}
                hasApiError={hasApiError}
                apiErrors={apiErrors}
                isLoading={props.isLoading}
            />
        </>
    );
};

export { AutoGenerateFormFieldResponsesFormModal, AutoGenerateFormFieldResponsesForm };
